import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Grid,
    IconButton,
    Tooltip
} from '@mui/material';
import { Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import timesheetService from '../services/timesheetService';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
import { useFormFieldErrors, REQUIRED_FIELDS_MESSAGE } from '../hooks/useFormFieldErrors';
import FormValidationAlert from './FormValidationAlert';

interface DateRangePickerProps {
    startDate: string;
    endDate: string;
    onRangeChange: (start: string, end: string) => void;
}

interface SavedRange {
    id: number;
    name: string;
    start_date: string;
    end_date: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ startDate, endDate, onRangeChange }) => {
    const [rangeType, setRangeType] = useState<string>('custom');
    const [savedRanges, setSavedRanges] = useState<SavedRange[]>([]);
    const [openSaveDialog, setOpenSaveDialog] = useState(false);
    const [newRangeName, setNewRangeName] = useState('');
    const fieldErrors = useFormFieldErrors();

    useEffect(() => {
        loadSavedRanges();
    }, []);

    const loadSavedRanges = async () => {
        try {
            const ranges = await timesheetService.getSavedRanges();
            setSavedRanges(ranges);
        } catch (err) {
            console.error('Failed to load saved ranges', err);
        }
    };

    const handleRangeTypeChange = (value: string) => {
        setRangeType(value);
        const today = new Date();
        const start = new Date(today);
        const end = new Date(today);

        switch (value) {
            case 'today':
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'yesterday':
                start.setDate(today.getDate() - 1);
                end.setDate(today.getDate() - 1);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'this_week':
                // Assuming Monday start
                const day = today.getDay();
                const diff = today.getDate() - day + (day === 0 ? -6 : 1);
                start.setDate(diff);
                end.setDate(start.getDate() + 6);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'last_week':
                const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
                const lwDay = lastWeek.getDay();
                const lwDiff = lastWeek.getDate() - lwDay + (lwDay === 0 ? -6 : 1);
                start.setDate(lwDiff - (today.getDate() - lastWeek.getDate())); // Correctly shift back
                // Actually easier:
                const currentMonday = new Date(today);
                currentMonday.setDate(today.getDate() - (today.getDay() || 7) + 1);
                start.setTime(currentMonday.getTime() - 7 * 24 * 60 * 60 * 1000);
                end.setTime(start.getTime() + 6 * 24 * 60 * 60 * 1000);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'this_month':
                start.setDate(1);
                end.setMonth(today.getMonth() + 1);
                end.setDate(0);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'last_month':
                start.setMonth(today.getMonth() - 1);
                start.setDate(1);
                end.setDate(0);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'this_year':
                start.setMonth(0, 1);
                end.setMonth(11, 31);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'last_year':
                start.setFullYear(today.getFullYear() - 1, 0, 1);
                end.setFullYear(today.getFullYear() - 1, 11, 31);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            // Quarters
            case 'q1_current':
                start.setMonth(0, 1);
                end.setMonth(2, 31);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'q2_current':
                start.setMonth(3, 1);
                end.setMonth(5, 30);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'q3_current':
                start.setMonth(6, 1);
                end.setMonth(8, 30);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'q4_current':
                start.setMonth(9, 1);
                end.setMonth(11, 31);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'q4_last':
                start.setFullYear(today.getFullYear() - 1, 9, 1);
                end.setFullYear(today.getFullYear() - 1, 11, 31);
                onRangeChange(formatDate(start), formatDate(end));
                break;
            case 'custom':
                // Do nothing, let user pick
                break;
            default:
                // Check if it's a saved range
                if (value.startsWith('saved_')) {
                    const rangeId = parseInt(value.split('_')[1]);
                    const range = savedRanges.find(r => r.id === rangeId);
                    if (range) {
                        onRangeChange(range.start_date, range.end_date);
                    }
                }
                break;
        }
    };

    const formatDate = (date: Date): string => {
        return date.toISOString().split('T')[0];
    };

    const handleSaveRange = async () => {
        if (!newRangeName.trim()) {
            fieldErrors.show(REQUIRED_FIELDS_MESSAGE, ['name']);
            return;
        }
        if (!startDate || !endDate) return;
        try {
            await timesheetService.saveReportRange(newRangeName, startDate, endDate);
            await loadSavedRanges();
            setOpenSaveDialog(false);
            setNewRangeName('');
            // Switch dropdown to the newly saved range? Maybe, but tricky to know ID immediately without precise return handling.
            // Just reloading for now.
        } catch (err) {
            console.error('Failed to save range', err);
        }
    };

    const handleDeleteRange = async (e: React.MouseEvent, rangeId: number) => {
        e.stopPropagation();
        try {
            await timesheetService.deleteSavedRange(rangeId);
            await loadSavedRanges();
            if (rangeType === `saved_${rangeId}`) {
                setRangeType('custom');
            }
        } catch (err) {
            console.error('Failed to delete range', err);
        }
    };

    return (
        <Box>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Date Range</InputLabel>
                        <Select
                            value={rangeType}
                            label="Date Range"
                            onChange={(e) => handleRangeTypeChange(e.target.value)}
                            MenuProps={getZoomCompensatedSelectMenuProps()}
                        >
                            <MenuItem value="custom">Custom</MenuItem>
                            <MenuItem disabled>--- Predefined ---</MenuItem>
                            <MenuItem value="today">Today</MenuItem>
                            <MenuItem value="yesterday">Yesterday</MenuItem>
                            <MenuItem value="this_week">This Week</MenuItem>
                            <MenuItem value="last_week">Last Week</MenuItem>
                            <MenuItem value="this_month">This Month</MenuItem>
                            <MenuItem value="last_month">Last Month</MenuItem>
                            <MenuItem value="this_year">This Year</MenuItem>
                            <MenuItem value="last_year">Last Year</MenuItem>
                            <MenuItem value="q1_current">Q1 (Current Year)</MenuItem>
                            <MenuItem value="q2_current">Q2 (Current Year)</MenuItem>
                            <MenuItem value="q3_current">Q3 (Current Year)</MenuItem>
                            <MenuItem value="q4_current">Q4 (Current Year)</MenuItem>
                            <MenuItem value="q4_last">Q4 (Last Year)</MenuItem>

                            {savedRanges.length > 0 && <MenuItem disabled>--- Saved Ranges ---</MenuItem>}
                            {savedRanges.map(range => (
                                <MenuItem key={range.id} value={`saved_${range.id}`}>
                                    <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                        <span>{range.name}</span>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleDeleteRange(e, range.id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} md={3}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                            onRangeChange(e.target.value, endDate);
                            setRangeType('custom');
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <TextField
                        fullWidth
                        size="small"
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                            onRangeChange(startDate, e.target.value);
                            setRangeType('custom');
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={() => setOpenSaveDialog(true)}
                        disabled={!startDate || !endDate}
                        fullWidth
                    >
                        Save Range
                    </Button>
                </Grid>
            </Grid>

            {/* Save Range Dialog */}
            <Dialog open={openSaveDialog} onClose={() => { setOpenSaveDialog(false); fieldErrors.clear(); }}>
                <DialogTitle>Save Date Range</DialogTitle>
                <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" gutterBottom>
                            Range: {startDate} to {endDate}
                        </Typography>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Range Name"
                            fullWidth
                            variant="outlined"
                            value={newRangeName}
                            error={fieldErrors.invalid('name')}
                            onChange={(e) => {
                                fieldErrors.clearField('name');
                                setNewRangeName(e.target.value);
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSaveDialog(false)}>Cancel</Button>
                    <Button onClick={handleSaveRange} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DateRangePicker;
